(function (root) {
  'use strict';

  if (!root.customElements || root.customElements.get('alvaura-cart-recommendations')) return;
  const emptyState = '<span class="cart-drawer__recommendations hidden" data-recommendations-state hidden aria-hidden="true"></span>';
  const events = ['cart:updated', 'DiscountUpdateEvent', 'GiftWrapUpdateEvent'];

  class AlvauraCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.seen = new WeakSet();
      this.onUpdate = (event) => {
        const data = event?.cartData || event?.detail?.data || event?.detail;
        if (data && typeof data === 'object') {
          if (this.seen.has(data)) return;
          this.seen.add(data);
        }
        this.version = (this.version || 0) + 1;
        clearTimeout(this.timer);
        // Pub/sub fires before native add rendering. Update synchronously so its
        // preservation fallback captures the new server result, including empty.
        if (this.applySections(data?.sections)) return;
        this.innerHTML = emptyState;
        this.timer = setTimeout(() => this.refresh(), 0);
      };
      events.forEach((name) => document.addEventListener(name, this.onUpdate));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onUpdate);
      }
      this.loadAutomatic();
    }

    disconnectedCallback() {
      this.version = (this.version || 0) + 1;
      clearTimeout(this.timer);
      events.forEach((name) => document.removeEventListener(name, this.onUpdate));
      this.unsubscribe?.();
    }

    applySections(sections) {
      if (!sections || typeof sections !== 'object') return false;
      const key = typeof sections[this.dataset.sectionId] === 'string' ? this.dataset.sectionId :
        Object.keys(sections).find((candidate) => candidate === 'cart-drawer' || candidate.includes('cart-drawer'));
      if (!key || typeof sections[key] !== 'string') return false;
      const parsed = new DOMParser().parseFromString(sections[key], 'text/html');
      const wrappers = parsed.querySelectorAll('alvaura-cart-recommendations');
      if (wrappers.length !== 1 || !wrappers[0].querySelector('[data-recommendations-state]')) return false;
      this.innerHTML = wrappers[0].innerHTML;
      this.loadAutomatic();
      return true;
    }

    async loadAutomatic() {
      const target = this.querySelector('[data-cart-related]');
      if (!target || target.dataset.requested === 'true') return;
      const seed = target.dataset.productId;
      if (!/^\d+$/.test(seed || '')) return;
      let cartIds;
      try { cartIds = new Set(JSON.parse(target.dataset.cartProductIds).map(String)); }
      catch (_) { return; }
      if (!cartIds.has(seed)) return;
      target.dataset.requested = 'true';
      const version = this.version;
      try {
        const url = new URL(target.dataset.url, root.location.href);
        if (url.origin !== new URL(root.location.href).origin) return;
        url.searchParams.set('section_id', 'alvaura-cart-related');
        url.searchParams.set('product_id', seed);
        url.searchParams.set('intent', 'related');
        url.searchParams.set('limit', '10');
        const preview = new URL(root.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url.toString(), { method: 'GET', headers: { Accept: 'text/html' } });
        if (!response.ok) return;
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!this.isConnected || this.version !== version || this.querySelector('[data-cart-related]') !== target) return;
        const result = parsed.querySelector('[data-cart-related-result]');
        if (!result) return;
        const items = target.querySelector('[data-cart-related-items]');
        if (!items) return;
        const seen = new Set(cartIds);
        if (target.dataset.giftProductId) seen.add(target.dataset.giftProductId);
        const limit = Math.max(1, Math.min(2, Number(target.dataset.limit) || 2));
        let count = 0;
        for (const card of result.querySelectorAll('[data-cart-related-product]')) {
          const id = card.dataset.cartRelatedProduct;
          if (!/^\d+$/.test(id || '') || seen.has(id)) continue;
          seen.add(id);
          items.appendChild(card);
          count += 1;
          if (count >= limit) break;
        }
        target.hidden = count === 0 && target.dataset.hasGift !== 'true';
      } catch (_) { /* No retry and no stale fallback. */ }
    }

    async refresh() {
      const version = this.version;
      if (!this.dataset.sectionId) return;
      try {
        // A read-only section render is needed only when a cart event omitted
        // sections. Never replay an add/change request or reuse stale cards.
        const url = new URL(root.location.href);
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', this.dataset.sectionId);
        const response = await fetch(url.toString(), { method: 'GET', headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const sections = await response.json();
        if (this.isConnected && this.version === version) this.applySections(sections);
      } catch (_) { /* Keep the explicit empty state; no automatic retry. */ }
    }
  }
  root.customElements.define('alvaura-cart-recommendations', AlvauraCartRecommendations);
})(typeof window === 'undefined' ? globalThis : window);
